import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { YearPlanResponse } from '../../api/generated';
import type { WeekCalculationStatuses } from '../../hooks/useWeekCalculationStatuses';
import { useStaffCompetences } from '../../hooks/useStaffCompetences';
import { formatDateStr } from '../../utils/dateUtils';
import { hasWeekStarted } from '../../utils/weekClassifier';
import type { StatusChipAction } from './WeekKpiCell/weekCalculationState';
import {
  resolveStatusChipAction,
  resolveWeekCalculationState
} from './WeekKpiCell/weekCalculationState';
import { StatusRow } from './WeekKpiCell/StatusRow';
import { ProfessionHoursRow } from './WeekKpiCell/ProfessionHoursRow';
import { PatientsBedsRow } from './WeekKpiCell/PatientsBedsRow';
import { WeekWarningIcon } from './WeekKpiCell/WeekWarningIcon';
import { computeWeekKpi } from './WeekKpiCell/computeWeekKpi';
import { OpenCalendarButton } from '../UI';
import styles from './GanttWeekHeader.module.css';

interface GanttWeekHeaderCornerProps {
  hasCalendarRow?: boolean;
}

export const GanttWeekHeaderCorner = ({ hasCalendarRow = false }: GanttWeekHeaderCornerProps) => {
  const { t } = useTranslation();

  return (
    <div className={`${styles.corner} ${hasCalendarRow ? styles.cornerTall : ''}`}>
      <div className={styles.cornerLabel} />
      <div className={styles.cornerKpi}>
        <div className={styles.cornerKpiRow}>{t('gantt-status')}</div>
        <div className={styles.cornerKpiRowDouble}>{t('gantt-employee-hours')}</div>
        <div className={styles.cornerKpiRow}>{t('gantt-patients-beds')}</div>
      </div>
      {hasCalendarRow && <div className={styles.cornerCalendarSpacer} />}
    </div>
  );
};

interface GanttWeekHeaderCellsProps {
  yearPlan: YearPlanResponse;
  showKpi?: boolean;
  onWeekStatusClick?: (weekNumber: number) => void;
  onOpenCalendar?: (weekNumber: number) => void;
  // Retries loading the statuses. Offered on UNKNOWN weeks in place of a recalculation.
  onRetryCalculationStatuses?: () => void;
  // Omitted by callers that render the header without KPIs, hence the no-statuses default below.
  weekCalculations?: WeekCalculationStatuses;
}

// `StatusRow`'s click prop: the week number is passed to every handler, and one of them ignores it.
type StatusChipClickHandler = (weekNumber: number) => void;

// Nothing fetched and nothing attempted: every status chip stays hidden, which is what the
// KPI-less preview header wants.
const NO_WEEK_CALCULATIONS: WeekCalculationStatuses = {
  statusByWeek: new Map(),
  hasAttempted: false,
  isUnavailable: false
};

export const GanttWeekHeaderCells = ({
  yearPlan,
  showKpi = true,
  onWeekStatusClick,
  onOpenCalendar,
  onRetryCalculationStatuses,
  weekCalculations = NO_WEEK_CALCULATIONS
}: GanttWeekHeaderCellsProps) => {
  const { t } = useTranslation();
  const { competences } = useStaffCompetences();
  const weekEntries = useMemo(() => {
    const today = new Date();
    return yearPlan.weeks.map((week) => {
      const isLocked = hasWeekStarted(week, today);
      return {
        week,
        isLocked,
        kpi: computeWeekKpi(
          yearPlan,
          week.weekNumber,
          competences,
          resolveWeekCalculationState(
            weekCalculations.statusByWeek.get(week.weekNumber),
            isLocked,
            weekCalculations.isUnavailable
          )
        )
      };
    });
  }, [yearPlan, competences, weekCalculations]);

  // What each chip action does, in one exhaustive table: a new `StatusChipAction` fails to typecheck
  // until it is handled here. The retry handler takes no week number, which a `(week: number) => void`
  // slot accepts, so both actions fit `StatusRow`'s single click prop.
  const statusClickHandlers: Record<StatusChipAction, StatusChipClickHandler | undefined> = {
    none: undefined,
    retry: onRetryCalculationStatuses,
    recalculate: onWeekStatusClick
  };

  // The compact preview has no room for the row, so the two flags are combined
  // once here rather than left to agree by convention.
  const hasCalendarRow = showKpi && onOpenCalendar !== undefined;

  return (
    <div
      className={`${styles.headerCells} ${showKpi ? '' : styles.headerCellsCompact} ${hasCalendarRow ? styles.headerCellsTall : ''}`}
    >
      {weekEntries.map(({ week, kpi, isLocked }) => {
        // The chip's wording comes from `StatusRow`'s presentation table, keyed on the same status,
        // so the label and the behaviour cannot disagree.
        const onStatusClick = statusClickHandlers[resolveStatusChipAction(kpi.calculation.status)];
        return (
          <div key={week.weekNumber} className={styles.weekCell}>
            <div className={styles.weekLabel}>
              <span className={styles.weekNumberAnchor}>
                <span className={styles.weekNumber}>U{week.weekNumber}</span>
                {showKpi && <WeekWarningIcon kpi={kpi} />}
              </span>
              {showKpi && (
                <span className={styles.weekRange}>
                  {formatDateStr(week.startDate)} - {formatDateStr(week.endDate)}
                </span>
              )}
            </div>
            {showKpi && (
              <div className={styles.weekKpi}>
                <div className={styles.weekKpiRow}>
                  {/* A locked week's status needs no server data, so it renders straight away; the
                      rest wait for the first fetch to settle rather than flashing "not calculated".
                      Settled, not succeeded: if the fetch failed the chip still appears as UNKNOWN,
                      because hiding it would silently remove the planner's only way in. */}
                  {(weekCalculations.hasAttempted || isLocked) && (
                    <StatusRow kpi={kpi} onStatusClick={onStatusClick} />
                  )}
                </div>
                <div className={styles.weekKpiRowDouble}>
                  <ProfessionHoursRow professions={kpi.professions} />
                </div>
                <div className={styles.weekKpiRow}>
                  <PatientsBedsRow kpi={kpi} />
                </div>
              </div>
            )}
            {hasCalendarRow && (
              <span className={styles.openCalendarSlot}>
                <OpenCalendarButton
                  label={t('week-overview-open-calendar-week', {
                    weekNumber: week.weekNumber
                  })}
                  onClick={() => onOpenCalendar(week.weekNumber)}
                />
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};
